import { useEffect, useState } from "react";
import "./page-navigation.scss";

import { triggerPostMoveFlash } from "@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash";
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { reorderWithEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { flushSync } from "react-dom";

import { GrabbableButton } from "./components";

import { isTemplatePageData, kToolbarHeight, TemplatePageType } from "models";

import { confirmPopup, ConfirmPopup } from "primereact/confirmpopup";
import { saveTemplate } from "utils/api";
import {
  addPage,
  deletePage,
  duplicatePage,
  reorderPage,
  selectPage,
  useAppDispatch,
  useAppSelector,
} from "utils/store";
import { Button, Icon, SidebarMenu } from "utils/ui";

import { CoverPdfPreview } from "../cover-pdf-preview";

export const PageNavigation = () => {
  const pagesState = useAppSelector((state) => state.templateBuilderSlice.pages);
  const selectedPageIndex = useAppSelector((state) => state.templateBuilderSlice.selectedPageIndex);
  const dispatch = useAppDispatch();

  const [showCoverPdf, setShowCoverPdf] = useState(false);

  useEffect(() => {
    return monitorForElements({
      canMonitor({ source }) {
        return isTemplatePageData(source.data);
      },
      onDrop({ location, source }) {
        const pages = [...pagesState];

        const target = location.current.dropTargets[0];
        if (!target) {
          return;
        }

        const sourceData = source.data;
        const targetData = target.data;

        if (!isTemplatePageData(sourceData) || !isTemplatePageData(targetData)) {
          return;
        }

        const indexOfSource = pages.findIndex((o) => o.id === sourceData.taskId);
        const indexOfTarget = pages.findIndex((o) => o.id === targetData.taskId);

        if (indexOfTarget < 0 || indexOfSource < 0) {
          return;
        }

        const closestEdgeOfTarget = extractClosestEdge(targetData);

        const reorderedList = reorderWithEdge({
          list: pages,
          startIndex: indexOfSource,
          indexOfTarget,
          closestEdgeOfTarget,
          axis: "vertical",
        });

        // Validate contents page is before defect list page
        const indexOfCover = reorderedList.findIndex((o) => o.type === TemplatePageType.coverPage);
        const indexOfContents = reorderedList.findIndex((o) => o.type === TemplatePageType.contentsPage);
        const indexOfDefectList = reorderedList.findIndex((o) => o.type === TemplatePageType.defectList);

        if (indexOfCover !== 0 || indexOfContents > indexOfDefectList) return;

        const newIndexOfSource = reorderedList.findIndex((o) => o.id === sourceData.taskId);
        const selectedPageIndex = newIndexOfSource > 0 ? newIndexOfSource : 1;

        // Using `flushSync` so we can query the DOM straight after this line
        flushSync(() => {
          dispatch(reorderPage({ pages: reorderedList, selectedPageIndex }));
        });
        // Being simple and just querying for the task after the drop.
        // We could use react context to register the element in a lookup,
        // and then we could retrieve that element after the drop and use
        // `triggerPostMoveFlash`. But this gets the job done.
        const element = document.querySelector(`[data-task-id="${sourceData.taskId}"]`);
        if (element instanceof HTMLElement) {
          triggerPostMoveFlash(element);
        }
      },
    });
  }, [pagesState, dispatch]);

  return (
    <div
      className="section-rail flex flex-col"
      style={{
        width: "var(--sidebar-width)",
        height: `calc(100vh - ${kToolbarHeight}px)`,
        borderRight: "1px solid var(--surface-border)",
      }}
    >
      <div className="flex-1 overflow-y-auto classy-scrollbar px-3 pb-4">
        <SidebarMenu.SubHeading className="text-center" title="START OF REPORT" />

        {pagesState.map((page, i) => {
          const nextPage = pagesState[i + 1];
          const isLastPage = i === pagesState.length - 1;
          const showAddIndicator =
            isLastPage ||
            nextPage?.type === TemplatePageType.contentsPage ||
            nextPage?.type === TemplatePageType.defectList;

          return (
            <GrabbableButton
              key={page.id}
              pageElement={page}
              selected={i === selectedPageIndex}
              showAddIndicator={showAddIndicator}
              onPageSelect={() => dispatch(selectPage(i))}
              onAddPage={() => {
                dispatch(addPage(i));
                dispatch(saveTemplate());
              }}
              onPageDuplicate={() => {
                dispatch(duplicatePage(i));
                dispatch(saveTemplate());
              }}
              onPageDelete={(event: any) => {
                confirmPopup({
                  target: event.currentTarget,
                  message: "Do you want to delete this section?",
                  icon: "pi pi-info-circle",
                  defaultFocus: "reject",
                  acceptClassName: "p-button-danger",
                  accept: () => {
                    dispatch(deletePage(i));
                    dispatch(saveTemplate());
                  },
                });
              }}
            />
          );
        })}

        <SidebarMenu.SubHeading className="text-center" title="END OF REPORT" />
      </div>

      <SidebarMenu.Divider />

      <div className="px-2 py-2">
        <SidebarMenu.Description>
          Click a <strong>＋ Add section</strong> line between any two sections to add your own custom section there.
          Fixed sections can&rsquo;t move or be deleted.
        </SidebarMenu.Description>

        <SidebarMenu.Description>
          Build: {process.env.REACT_APP_SENTRY_RELEASE}
        </SidebarMenu.Description>

        {process.env.NODE_ENV === "development" && (
          <Button
            variant="secondary"
            buttonSize="small"
            className="w-full mt-2"
            title="Dev only: render the cover page to a real PDF client-side, no server round-trip"
            onClick={() => setShowCoverPdf(true)}
          >
            <Icon name="Eye" size={14} strokeWidth={2} />
            Preview Cover (PDF)
          </Button>
        )}
      </div>

      <CoverPdfPreview visible={showCoverPdf} onHide={() => setShowCoverPdf(false)} />

      <ConfirmPopup />
    </div>
  );
};
